export async function uploadVideoMultipart(file, API_URL, onProgress) {
  const MIN_PART_SIZE = 5 * 1024 * 1024; // 5 MB (S3 rule)
  const OPTIMAL_PART_SIZE = 8 * 1024 * 1024; // 8 MB chunks for speed
  const CHUNK_SIZE = Math.max(MIN_PART_SIZE, OPTIMAL_PART_SIZE);

  // 1. Start multipart upload
  const startRes = await fetch(`${API_URL}api/upload/start-multipart`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filename: file.name, contentType: file.type }),
  });
  const { uploadId, key } = await startRes.json();

  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let uploadedCount = 0;
  const parts = [];

  // Helper: upload one part with retry
  const uploadPart = async (i, retries = 3) => {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blob = file.slice(start, end);

    // Get presigned URL
    const urlRes = await fetch(
      `${API_URL}api/upload/presign-part?key=${encodeURIComponent(
        key
      )}&uploadId=${uploadId}&partNumber=${i + 1}`
    );
    const { url } = await urlRes.json();

    try {
      const uploadRes = await fetch(url, { method: "PUT", body: blob });
      if (!uploadRes.ok) throw new Error(`Failed part ${i + 1}`);

      const ETag = uploadRes.headers.get("ETag");
      uploadedCount++;
      if (onProgress) {
        onProgress(Math.round((uploadedCount / totalChunks) * 100));
      }
      return { ETag, PartNumber: i + 1 };
    } catch (err) {
      if (retries > 0) {
        const delay = (4 - retries) * 500; // exponential backoff
        await new Promise((r) => setTimeout(r, delay));
        return uploadPart(i, retries - 1);
      }
      throw err;
    }
  };

  // Dynamic concurrency: min(10, totalChunks)
  const CONCURRENCY = Math.min(10, totalChunks);

  // Upload queue manager
  const queue = Array.from({ length: totalChunks }, (_, i) => i);
  async function runQueue() {
    const results = [];
    while (queue.length > 0) {
      const chunkGroup = queue.splice(0, CONCURRENCY);
      const res = await Promise.all(chunkGroup.map(uploadPart));
      results.push(...res);
    }
    return results;
  }

  const partsResult = await runQueue();
  parts.push(...partsResult);

  // 3. Complete multipart upload
  const completeRes = await fetch(`${API_URL}api/upload/complete-multipart`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key, uploadId, parts }),
  });
  const data = await completeRes.json();
  return data.key;
}
